import { useEffect, useState } from 'react';
import { useCartV2, useGlobalData } from 'contexts';
import { useRouter } from 'next/router';
import { DiscoveryKeyword } from '@teko/discovery';
import { trackManually, trackSearchEvent } from '@teko/tracking';
import { useKeywords } from 'hooks';

interface Option {
  numberOfHistories?: number;
  limitKeywords?: number;
}

interface TargetPath {
  pathname: string;
  query: {
    router: string;
    query: string;
    masterCategoryIds?: string;
  };
}

export const useSearchSuggestions = ({
  numberOfHistories = 5,
  limitKeywords,
}: Option = {}) => {
  const { isMobile } = useGlobalData();
  const router = useRouter();
  const searchHistoryKey = `${isMobile ? 'MOBILE' : 'DESKTOP'}_SEARCH_HISTORY`;
  const initKeyword = (router.query.query as string) || '';
  const { onAddSingleItem, onRequestItemSku } = useCartV2();
  const [keyword, setKeyword] = useState(initKeyword);
  const [histories, setHistories] = useState<DiscoveryKeyword[]>([]);
  const { result: suggestions, isSuggestProduct } = useKeywords(
    keyword,
    limitKeywords,
  );

  const getSearchHistory = (count = 5): DiscoveryKeyword[] => {
    if (typeof localStorage !== 'undefined') {
      const historyStorage = localStorage.getItem(searchHistoryKey);
      if (historyStorage) {
        const data = JSON.parse(historyStorage);
        return (data || []).reverse().splice(0, count);
      }
      return [];
    }
    return [];
  };
  const saveSearchHistory = (keyword: DiscoveryKeyword): void => {
    if (!keyword) return;
    const historyStorage = localStorage.getItem(searchHistoryKey);
    if (!historyStorage) {
      localStorage.setItem(searchHistoryKey, JSON.stringify([keyword]));
      return;
    }
    let historyArr = JSON.parse(historyStorage);
    historyArr = historyArr.filter(
      (item: DiscoveryKeyword) =>
        JSON.stringify(item) !== JSON.stringify(keyword),
    );
    historyArr = [...historyArr, keyword];
    localStorage.setItem(searchHistoryKey, JSON.stringify(historyArr));
    setHistories(getSearchHistory(numberOfHistories));
  };
  const removeSearchHistories = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(searchHistoryKey, JSON.stringify([]));
      setHistories([]);
    }
  };

  const onSearch = (keywordObj: DiscoveryKeyword): void => {
    saveSearchHistory(keywordObj);
    let targetPath: TargetPath = null;
    if (router.query.query) {
      targetPath = {
        pathname: '/search',
        query: {
          router: 'productListing',
          query: keywordObj.query,
          ...(!!keywordObj.categoryId
            ? {
                masterCategoryIds: keywordObj.categoryId.toString(),
              }
            : {}),
        },
      };
      router.replace(targetPath).then(() => window.scrollTo(0, 0));
    } else {
      targetPath = {
        pathname: '/search',
        query: {
          router: 'productListing',
          query: keywordObj.query,
          ...(!!keywordObj.categoryId
            ? {
                masterCategoryIds: keywordObj.categoryId.toString(),
              }
            : {}),
        },
      };
      router.push(targetPath).then(() => window.scrollTo(0, 0));
    }
    const targetUrl = new URL(window.location.origin);
    targetUrl.pathname = targetPath.pathname;
    targetUrl.search = new URLSearchParams(targetPath.query).toString();
    trackSearchEvent(keywordObj.query);
    trackManually(
      'searchButton',
      'headerBar',
      'searchProductResult',
      JSON.stringify({
        searchKeyword: keywordObj.query,
        href: window.location.href,
        url: targetUrl.href,
      }),
    );
  };

  const addToCart = async (product: DiscoveryKeyword, quantity = 1) => {
    if (!!product.sku) {
      await onAddSingleItem({ sku: product.sku, quantity }, true);
    } else {
      await onRequestItemSku(product.highlight, quantity);
    }
  };

  const onRedirectDetailProduct = (sku: string): void => {
    if (sku) {
      router.push(`/products/${sku}`);
    }
  };

  useEffect(() => {
    if (numberOfHistories > 0) {
      setHistories(getSearchHistory(numberOfHistories));
    } else setHistories([]);
  }, [numberOfHistories]);

  return {
    keyword,
    setKeyword,
    histories,
    removeSearchHistories,
    suggestions: suggestions,
    onSearch,
    onRedirectDetailProduct,
    isSuggestProduct,
    addToCart,
  };
};
